import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Phone, MapPin } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

// Braises générées de façon déterministe (pas de Math.random pour un rendu stable)
const EMBERS = Array.from({ length: 16 }, (_, i) => ({
  left: (i * 61) % 100,
  delay: ((i * 7) % 22) / 3.2,
  duration: 6.5 + (i % 5) * 1.4,
  size: 3 + (i % 4) * 2,
  drift: ((i % 2 === 0 ? 1 : -1) * (14 + (i * 13) % 40)),
}));

const HeroFullscreen = () => {
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const subheadRef = useRef(null);
  const ctaRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const headline = headlineRef.current;
    const subhead = subheadRef.current;
    const cta = ctaRef.current;
    const bg = bgRef.current;

    if (!section || !headline || !subhead || !cta || !bg) return;

    const ctx = gsap.context(() => {
      // Animation de chargement initial
      const loadTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      loadTl
        .fromTo(bg,
          { scale: 1.1, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1.2 }
        )
        .fromTo(headline.children,
          { y: 100, opacity: 0, rotateX: 45 },
          { y: 0, opacity: 1, rotateX: 0, stagger: 0.08, duration: 1 },
          '-=0.8'
        )
        .fromTo(subhead,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8 },
          '-=0.5'
        )
        .fromTo(cta.children,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.15, duration: 0.6 },
          '-=0.4'
        );

      // Animation simple au scroll (sans pin)
      gsap.to(headline, {
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
        y: -100,
        opacity: 0.3,
        ease: 'none'
      });

      gsap.to(subhead, {
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
        y: -80,
        opacity: 0.3,
        ease: 'none'
      });

      gsap.to(cta, {
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
        y: -50,
        opacity: 0,
        ease: 'none'
      });

      gsap.to(bg, {
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
        scale: 1.2,
        ease: 'none'
      });

    }, section);

    return () => ctx.revert();
  }, []);

  // Parallax 3D : gyroscope sur téléphone, souris sur ordinateur.
  // Le fond et le titre bougent en sens opposés → effet de profondeur.
  useEffect(() => {
    const section = sectionRef.current;
    const headline = headlineRef.current;
    const bg = bgRef.current;
    if (!section || !headline || !bg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const bgX = gsap.quickTo(bg, 'x', { duration: 0.8, ease: 'power2.out' });
    const bgY = gsap.quickTo(bg, 'y', { duration: 0.8, ease: 'power2.out' });
    const hX = gsap.quickTo(headline, 'x', { duration: 0.9, ease: 'power2.out' });

    const clamp = (v, m) => Math.max(-m, Math.min(m, v));
    let baseBeta = null;

    const onOrientation = (e) => {
      if (e.gamma == null || e.beta == null) return;
      if (baseBeta == null) baseBeta = e.beta; // position de repos = référence
      const gx = clamp(e.gamma, 22);
      const gy = clamp(e.beta - baseBeta, 22);
      bgX(gx * -0.9);
      bgY(gy * -0.9);
      hX(gx * 0.45);
    };

    const onMouse = (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2;
      const ny = (e.clientY / window.innerHeight - 0.5) * 2;
      bgX(nx * -18);
      bgY(ny * -12);
      hX(nx * 8);
    };

    const attachGyro = () => window.addEventListener('deviceorientation', onOrientation);

    // iOS demande une permission explicite, déclenchée par le premier toucher
    const askPermission = () => {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then((state) => { if (state === 'granted') attachGyro(); })
          .catch(() => {});
      }
      window.removeEventListener('touchend', askPermission);
    };

    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    if (isTouch) {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        window.addEventListener('touchend', askPermission);
      } else {
        attachGyro(); // Android : pas de permission requise
      }
    } else {
      window.addEventListener('mousemove', onMouse);
    }

    return () => {
      window.removeEventListener('deviceorientation', onOrientation);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('touchend', askPermission);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen overflow-hidden"
      style={{ margin: 0, padding: 0 }}
    >
      {/* Image de fond plein écran — débord de 28px pour le parallax gyroscopique */}
      <div
        ref={bgRef}
        className="absolute"
        style={{ willChange: 'transform', top: -28, left: -28, right: -28, bottom: -28 }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/images/hero-burger.webp`}
          alt="Le Family's Burger"
          className="w-full h-full object-cover"
          loading="eager"
          style={{ objectPosition: 'center', backgroundColor: '#7c2d12' }}
        />
        {/* Effet vignette */}
        <div className="absolute inset-0 bg-radial-vignette" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
      </div>

      {/* Braises qui montent (barbecue) */}
      <div className="hero-embers" aria-hidden="true">
        {EMBERS.map((e, i) => (
          <span
            key={i}
            className="hero-ember"
            style={{
              left: `${e.left}%`,
              width: e.size,
              height: e.size,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`,
              '--drift': `${e.drift}px`,
            }}
          />
        ))}
      </div>

      {/* Contenu */}
      <div className="relative z-10 w-full h-full flex flex-col justify-between p-6 md:p-12 lg:p-16 pt-32 md:pt-40">
        {/* Contenu principal */}
        <div className="flex-1 flex flex-col justify-center max-w-6xl">
          <h1
            ref={headlineRef}
            className="font-black text-[15vw] sm:text-[12vw] md:text-[10vw] lg:text-[8vw] text-white leading-[0.9] tracking-tight mb-6"
            style={{
              perspective: '1000px',
              textShadow: '0 8px 32px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.6)'
            }}
          >
            <span className="inline-block">LE</span>{' '}
            <span className="inline-block">GOÛT</span>{' '}
            <span className="inline-block">QUI</span>{' '}
            <span className="inline-block text-shimmer">RASSEMBLE</span>
          </h1>

          <p
            ref={subheadRef}
            className="text-base sm:text-xl md:text-2xl lg:text-3xl text-white/90 max-w-2xl font-medium"
            style={{ textShadow: '0 4px 16px rgba(0,0,0,0.8)' }}
          >
            Burgers artisanaux, tacos savoureux et desserts gourmands — 100% halal, à Bourg-en-Bresse
          </p>

          <div ref={ctaRef} className="mt-10 flex flex-wrap gap-4">
            <a
              href={restaurantInfo.orderLink}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-shine cta-glow inline-flex items-center gap-3 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base uppercase tracking-wide rounded-full transition-all duration-300 shadow-2xl"
            >
              Commander
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href={`tel:${restaurantInfo.phone}`}
              className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/40 backdrop-blur-sm text-white font-bold text-base uppercase tracking-wide rounded-full hover:bg-white/20 transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              Appelez-nous
            </a>
            <Link
              to="/menu"
              className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/40 backdrop-blur-sm text-white font-bold text-base uppercase tracking-wide rounded-full hover:bg-white/20 transition-all duration-300"
            >
              <MapPin className="w-5 h-5" />
              Voir le menu
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-end">
          <span className="text-xs text-white/60 uppercase tracking-widest font-semibold">
            Bourg-en-Bresse
          </span>
          <span className="text-xs text-white/60 uppercase tracking-widest font-semibold animate-pulse">
            Scroll ↓
          </span>
        </div>
      </div>

      <style>{`
        .bg-radial-vignette {
          background: radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%);
        }
        /* Braises montantes */
        .hero-embers {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          z-index: 5;
        }
        .hero-ember {
          position: absolute;
          bottom: -12px;
          border-radius: 9999px;
          background: radial-gradient(circle, #ffe3b3 0%, #ff8a3d 45%, rgba(255,80,0,0) 75%);
          opacity: 0;
          animation-name: emberRise;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform, opacity;
        }
        @keyframes emberRise {
          0%   { transform: translate3d(0, 0, 0) scale(1); opacity: 0; }
          8%   { opacity: .95; }
          55%  { opacity: .55; }
          100% { transform: translate3d(var(--drift, 20px), -105vh, 0) scale(.25); opacity: 0; }
        }
        /* Titre RASSEMBLE : dégradé animé */
        .text-shimmer {
          background: linear-gradient(90deg, #fb923c, #ffd9a0, #f97316, #fb923c);
          background-size: 250% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-shadow: none;
          animation: heroShimmer 5s linear infinite;
        }
        @keyframes heroShimmer {
          to { background-position: -250% 0; }
        }
        /* Bouton Commander : balayage lumineux + halo pulsé */
        .cta-shine { position: relative; overflow: hidden; }
        .cta-shine::after {
          content: '';
          position: absolute;
          top: 0; left: -80%;
          width: 55%; height: 100%;
          background: linear-gradient(105deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
          transform: skewX(-20deg);
          animation: ctaSweep 3.8s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes ctaSweep {
          0%, 55% { left: -80%; }
          80%, 100% { left: 140%; }
        }
        .cta-glow { animation: ctaGlow 2.6s ease-in-out infinite; }
        @keyframes ctaGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,.45), 0 25px 50px -12px rgba(0,0,0,.5); }
          50%      { box-shadow: 0 0 34px 4px rgba(249,115,22,.65), 0 25px 50px -12px rgba(0,0,0,.5); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-ember, .cta-shine::after { display: none; }
          .cta-glow, .text-shimmer { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default HeroFullscreen;
